/**
 * Platform-admin tasklist read client — LexPortal's transport, and the only
 * tasklist code this SPA still owns.
 *
 * Mirrors the SHIPPED fs-browser proxy pattern (`lib/fs-browser-api.ts`):
 * a thin REST call through lexserver's platform.admin-gated per-user runtime
 * proxy — deliberately NOT `useGatewayRpc` (handoff §3).
 *
 *   LexPortal ──▶ GET /api/platform/runtimes/:userId/tasklist[/:taskId]
 *                 (platform.admin; resolveUserRuntimeBaseUrl + proxy)
 *                      └──▶ the runtime's read-only tasklist HTTP handlers
 *
 * The shapes, the ordering rule and the grouping come from
 * `@lexiun/tasklist-core` — shared with the LexIDE pane and reconciled to the
 * runtime, so no comparator lives here. READ-ONLY (v1); the runtime
 * `tasklist.*` methods make write trivially addable later.
 *
 * ── Wire encoding (verified against the live route, 2026-07-23) ─────────────
 * `status` / `priority` are LISTS in the frozen contract, and the runtime
 * handler parses them as COMMA-SEPARATED values (`?status=open,waiting` →
 * `parseEnumList` splits on ","; unknown members are silently dropped).
 * Repeated `?status=a&status=b` params do NOT work — only the first is read —
 * so arrays MUST be joined, never appended. `caseId` / `due` /
 * `includeArchived` are scalars.
 */

import { getLexiunToken } from "./lexiun-jwt";
import { newTraceparent } from "./traceparent";
import type { TaskDetailResult, TaskRow, TasklistFilters } from "@lexiun/tasklist-core";

const LEXSERVER_BASE = "/lexserver";

/** `{ tasks }` — the list route's response, in the server's canonical order. */
export interface TasklistListResult {
  tasks: TaskRow[];
}

export class TasklistApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "TasklistApiError";
  }
}

/** A task that no longer exists (deleted/archived between list and click).
 *  The live route answers 404 for an unknown taskId — a NORMAL outcome the
 *  detail pane renders as "not found", never as a transport failure. */
export const TASK_NOT_FOUND = "TASK_NOT_FOUND";

/**
 * GET through the platform proxy. Tolerates BOTH the raw shape (§4 documents
 * `{tasks}` / `{task,history,historyTotal}` — which is what the route actually
 * returns today) and lexserver's `{ok,data}` envelope, so a later change to
 * the standard envelope does not break this call site.
 */
async function tasklistGet<T>(path: string): Promise<T> {
  const token = await getLexiunToken();
  const headers: Record<string, string> = { traceparent: newTraceparent() };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${LEXSERVER_BASE}${path}`, { headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string } | string;
      message?: string;
    };
    const err = typeof body.error === "object" ? body.error : undefined;
    throw new TasklistApiError(
      res.status === 404
        ? TASK_NOT_FOUND
        : (err?.code ?? (typeof body.error === "string" ? body.error : "TASKLIST_ERROR")),
      err?.message ?? body.message ?? res.statusText,
      res.status,
    );
  }
  const json = (await res.json()) as unknown;
  // Unwrap `{ok:true,data:{…}}` when enveloped; otherwise use the body.
  if (json && typeof json === "object" && "ok" in json && "data" in json) {
    return (json as { data: T }).data;
  }
  return json as T;
}

/**
 * Serialize the filters onto the query string. Exported for unit test —
 * the list/scalar split is a real wire contract, not a formatting detail
 * (see the header note).
 */
export function buildTasklistQuery(filters: TasklistFilters): string {
  const qs = new URLSearchParams();
  if (filters.status?.length) qs.set("status", filters.status.join(","));
  if (filters.priority?.length) qs.set("priority", filters.priority.join(","));
  if (filters.caseId) qs.set("caseId", filters.caseId);
  if (filters.due && filters.due !== "any") qs.set("due", filters.due);
  if (filters.includeArchived) qs.set("includeArchived", "true");
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/**
 * List a user's tasklist in the server's CANONICAL order — never re-sort,
 * only group (`groupTasks` from the shared package).
 */
export async function listTasklist(
  userId: string,
  filters: TasklistFilters = {},
): Promise<TasklistListResult> {
  const data = await tasklistGet<TasklistListResult>(
    `/api/platform/runtimes/${encodeURIComponent(userId)}/tasklist${buildTasklistQuery(filters)}`,
  );
  return { tasks: data.tasks ?? [] };
}

/**
 * One task + a PAGE of its append-only ledger (newest-first). `historyBefore`
 * is the `at` of the oldest row already shown; `historyTotal` sizes the full
 * ledger so the caller knows when more pages remain.
 */
export async function getTaskDetail(
  userId: string,
  taskId: string,
  opts: { historyLimit?: number; historyBefore?: string } = {},
): Promise<TaskDetailResult> {
  const qs = new URLSearchParams();
  if (opts.historyLimit != null) qs.set("historyLimit", String(opts.historyLimit));
  if (opts.historyBefore) qs.set("historyBefore", opts.historyBefore);
  const q = qs.toString();
  return tasklistGet<TaskDetailResult>(
    `/api/platform/runtimes/${encodeURIComponent(userId)}/tasklist/${encodeURIComponent(taskId)}${
      q ? `?${q}` : ""
    }`,
  );
}
